import {
  Box,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  tooltipClasses,
  type TooltipProps,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import type { ReactNode } from 'react';

export interface DetailRow {
  label: string;
  value: ReactNode;
}

interface DetailsInfoTooltipProps {
  /** Heading shown at the top of the popover. Defaults to "Details". */
  title?: string;
  /** Label/value rows rendered inside the popover. */
  rows: DetailRow[];
  /** Tooltip placement; defaults to bottom-start to align under the icon. */
  placement?: TooltipProps['placement'];
}

/**
 * Wider, light-themed `Tooltip` used to host record details. The default
 * MUI tooltip is too narrow for label/value rows, so this variant lifts
 * the background to paper, adds a soft border + shadow, and gives it
 * enough width for the typical 3–6 rows of metadata.
 */
const StyledTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 360,
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: theme.shadows[3],
    padding: theme.spacing(1.5, 2),
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.palette.background.paper,
    '&::before': { border: `1px solid ${theme.palette.divider}` },
  },
}));

/** Single label/value row inside the popover. */
function DetailRowView({ label, value }: DetailRow) {
  return (
    <Stack direction="row" justifyContent="space-between" spacing={3}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={600} component="div">
        {value ?? '—'}
      </Typography>
    </Stack>
  );
}

/**
 * Info-icon button that opens a styled popover with record metadata.
 * Used next to detail-page titles so the date / id / status block can
 * live out of the way until the user wants it.
 */
export function DetailsInfoTooltip({
  title = 'Details',
  rows,
  placement = 'bottom-start',
}: DetailsInfoTooltipProps) {
  return (
    <StyledTooltip
      placement={placement}
      title={
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {title}
          </Typography>
          <Stack spacing={0.75}>
            {rows.map((row) => (
              <DetailRowView key={row.label} {...row} />
            ))}
          </Stack>
        </Box>
      }
    >
      <IconButton size="small" aria-label="show details" sx={{ color: 'text.secondary' }}>
        <InfoOutlinedIcon fontSize="small" />
      </IconButton>
    </StyledTooltip>
  );
}
